import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, ReactNode } from "react";
import { connect, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";

import loadingGif from "assets/gif/loading.gif";

import {
  fetchDatasets,
  fetchSortedDatasets,
  setEnrichingDataset,
  setFilterData,
} from "redux/Datasets/actions";
import {
  getDatasets,
  getContentLoading,
  getSampleData,
  getDeleteModalId,
  getEnrichingDataset,
  getFilterData,
} from "redux/Datasets/selectors";
import { getUserConfig, getUserInfo } from "redux/User/selectors";
import { State } from "redux/store";
import { selectUser, isAnonymousUser, userLogout } from "redux/authReducer";
import {
  getShowDSCreationWarning,
  getShowRequestDemoModal,
} from "redux/Modals/selectors";
import { setShowDSCreationWarning } from "redux/Modals/actions";

import {
  useWindowResize,
  calculatePlaceHolderCount,
} from "helpers/utility/utilities";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { PRIVATE_DATASETS_PATH } from "helpers/constants/paths";
import { AMP_DATASET_OPENED } from "helpers/constants/amplitudeEvents";
import {
  AMP_DATASET_ID,
  AMP_DATASET_NAME,
  AMP_DATASET_SOURCE,
} from "helpers/constants/amplitudeProperties";
import {
  Dataset,
  ContactUsComponentType,
  DatasetSortEnum,
  DatasetStatusEnum,
  DatasetTypeEnum,
  EnrichingDS,
  InventoryFilterData,
} from "types";
import { FETCH_ENRICHMENT_PROGRESS_INTERVAL } from "helpers/constants/miscellaneous";

import Header from "views/layout/Header";
import ContentLoad from "views/uikit/ContentLoad";

import InternalServerErrorModal from "views/modals/InternalServerErrorModal";
import { NoMatchFoundCard } from "views/components/NoMatchFoundCard";
import CreateDatasetModal from "views/modals/CreateDatasetModal";
import SignupSuccessModal from "views/modals/SignupSuccessModal";
import DatasetErrorModal from "views/modals/DatasetErrorModal";
import LimitExceedModal from "views/modals/LimitExceedModal";
import UpgradePlanModal from "views/modals/UpgradePlanModal";
import RequestDemoModal from "views/modals/RequestDemoModal";
import FreePlanModal from "views/modals/FreePlanModal";
import ProPlanModal from "views/modals/ProPlanModal";
import SignupModal from "views/modals/SignupModal";
import ErrorModal from "views/modals/ErrorModal";
import DatasetsFilters from "./DatasetsFilters";

import AddDatasetCard from "./AddDatasetCard";
import DatasetCard from "./DatasetCard";

import styles from "./style.module.scss";

import { toast } from "react-toastify";
import VLToastComponent from "views/components/VLToastComponent";

const STATUS_ORDER: { [key in DatasetStatusEnum]: number } = {
  [DatasetStatusEnum.INDEXING]: 0,
  [DatasetStatusEnum.ENRICHING]: 0,
  [DatasetStatusEnum.UPLOADING]: 0,
  [DatasetStatusEnum.NEW]: 0,
  [DatasetStatusEnum.READY]: 1,
  [DatasetStatusEnum.INITIALIZING]: 2, // Resume
  [DatasetStatusEnum.FATAL_ERROR]: 3,
  [DatasetStatusEnum.PRE_PROCESSING]: 0,
  [DatasetStatusEnum.SAVING]: 0
};

const DatasetInventory = ({
  datasets,
  userConfig,
  filterData,
  deleteModalId,
  isContentLoading,
  showRequestDemoModal,
  userId,
  enrichingDataset,
  showDSCreationWarning,
}: DatasetInventoryTypes) => {
  const [filteredDatasets, setFilteredDatasets] = useState(datasets);

  const [dropdownBoxDisplay, setdropdownBoxDisplay] = useState(false);
  const [count, setCount] = useState(0);
  const [showDatasetTypeDropdown, setShowDatasetTypeDropdown] = useState(false);
  const [cardOffsetWidth, setCardOffsetWidth] = useState(0);

  const cardContainer = useRef<HTMLDivElement>(null);
  const cardElement = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterOptionsRef = useRef<HTMLDivElement>(null);
  const optionsDropdownButtonRef = useRef<HTMLDivElement>(null);
  const previousResultRef = useRef<any>([]);
  const toastIdRef = useRef<any>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pathName = useLocation().pathname;
  const { height, width } = useWindowResize();

  const user = selectUser();

  const dispatchFilterData = (data: InventoryFilterData) => {
    dispatch(setFilterData(data));
  };

  const showToast = (Element: ReactNode) => {
    const DEFAULT_TOAST_SETTINGS: any = {
      autoClose: false,
      closeButton: false,
      closeOnClick: true,
      position: "bottom-center",
    };

    if (toastIdRef.current) {
      toast.update(toastIdRef.current, {
        ...DEFAULT_TOAST_SETTINGS,
        render: Element,
      });
    } else {
      toastIdRef.current = toast(Element, DEFAULT_TOAST_SETTINGS);
    }
  };

  const handleToastClose = () => {
    toast.dismiss(toastIdRef.current);
    toastIdRef.current = null;
  };

  const handleShowOnPremGuide = () => {
    window.open(
      "https://docs.visual-layer.com/docs/on-prem/operation",
      "_blank"
    );
    dispatch(setShowDSCreationWarning(false));
  };

  const isPrivatePath = pathName === PRIVATE_DATASETS_PATH || pathName === "/";

  const dynamicTitle = isPrivatePath
    ? "Dataset Inventory"
    : "Public VL Datasets";

  const isAnyFilterSelected =
    filterData.searchValue ||
    filterData.sortValue !== DatasetSortEnum.STATUS ||
    filterData.datasetType !== DatasetTypeEnum.All ||
    filterData.datasetStatus.filter((status) => status.selected).length > 0;

  const length = filteredDatasets.length;

  const datasetsCountText = `Displaying ${
    datasets.length === filteredDatasets.length ? "All" : "Filtered"
  } Datasets (${filteredDatasets.length}/${datasets.length})`;

  const handleOutsideClick = (event: any) => {
    const optionDropdownButton = optionsDropdownButtonRef.current;
    const options = filterOptionsRef.current;

    const clickedInOptions = options && !options.contains(event.target);
    const clickedOnOptionDropdown =
      optionDropdownButton && !optionDropdownButton.contains(event.target);
    if (
      showDatasetTypeDropdown &&
      clickedInOptions &&
      clickedOnOptionDropdown
    ) {
      setShowDatasetTypeDropdown(false);
    }

    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setdropdownBoxDisplay(false);
    }
  };

  const renderInventoryInfo = () => {
    return (
      <DatasetsFilters
        filterData={filterData}
        setFilterData={dispatchFilterData}
        datasetsCountText={datasetsCountText}
      />
    );
  };

  const handleClearFilters = () => {
    const unselectedDatasetStatus = filterData.datasetStatus.map((status) => {
      return { ...status, selected: false };
    });

    dispatchFilterData({
      datasetStatus: unselectedDatasetStatus,
      datasetType: DatasetTypeEnum.All,
      sortValue: DatasetSortEnum.STATUS,
      searchValue: "",
    });
    setFilteredDatasets([]);
  };

  const setCardWitdh = (value: number) => {
    setCardOffsetWidth(value);
  };

  useEffect(() => {
    let result = [...datasets];
    const { datasetStatus, datasetType, searchValue, sortValue } = filterData;

    const selectedStatusValues = datasetStatus
      .filter((status) => status.selected)
      .map((status) => status.id);

    if (selectedStatusValues.length > 0) {
      result = result.filter((dataset) =>
        selectedStatusValues.includes(dataset.status)
      );
    }

    if (searchValue) {
      result = result.filter((dataset) => {
        const datasetName: string = dataset.display_name.toLowerCase();
        const searchText = searchValue.toLowerCase();

        return datasetName.includes(searchText) || false;
      });
    }

    if (datasetType === DatasetTypeEnum.Private) {
      result = result.filter((dataset) => !dataset.sample);
    } else if (datasetType === DatasetTypeEnum.Public) {
      result = result.filter((dataset) => dataset.sample);
    }

    switch (sortValue) {
      case DatasetSortEnum.SIZE:
        result = result.sort(
          (datasetA, datasetB) => datasetB.size_bytes - datasetA.size_bytes
        );
        break;

      case DatasetSortEnum.NAME:
        result = result.sort((datasetA, datasetB) => {
          return datasetA.display_name.localeCompare(datasetB.display_name);
        });
        break;

      case DatasetSortEnum.DATE:
        result = result.sort(
          (datasetA, datasetB) =>
            new Date(datasetB.created_at).getTime() -
            new Date(datasetA.created_at).getTime()
        );
        break;

      case DatasetSortEnum.STATUS:
        result = result.sort((datasetA, datasetB) => {
          // Primary sorting by status order
          const statusA = STATUS_ORDER[datasetA.status];
          const statusB = STATUS_ORDER[datasetB.status];

          if (statusA !== statusB) {
            return statusA - statusB;
          }

          // Secondary sorting by creation date for datasets with the same status group
          return (
            new Date(datasetB.created_at).getTime() -
            new Date(datasetA.created_at).getTime()
          );
        });

        break;
    }

    if (JSON.stringify(result) !== JSON.stringify(previousResultRef.current)) {
      setFilteredDatasets(result);
      previousResultRef.current = result;
    }
  }, [filterData, datasets]);

  useEffect(() => {
    const extraWidth = 60;
    const recordsCount = isPrivatePath ? length + 1 : length;
    const card = cardElement.current
      ? cardElement.current
      : length > 0
        ? { offsetWidth: cardOffsetWidth }
        : null;

    let tempCount = calculatePlaceHolderCount(
      cardContainer.current,
      card, //in case the ref is null, try using cardOffsetWidth
      recordsCount,
      extraWidth
    );
    if (count !== tempCount) {
      setCount(tempCount);
    }
    //eslint-disable-next-line
  }, [height, width, count, length, datasets, filterData, cardOffsetWidth]);

  useEffect(() => {
    let fetchDatasetsTask: any;
    const isUserConfigReady = () => userConfig && userConfig.length > 0;

    if (
      isPrivatePath &&
      process.env.REACT_APP_IS_AUTHENTICATION_DISABELED === "false" &&
      ((user && isAnonymousUser()) || !user)
    ) {
      userLogout();
    }

    // Check if userConfig is initially filled, if not, check again
    if (!isUserConfigReady()) {
      const checkUserConfigInterval = setInterval(() => {
        if (isUserConfigReady()) {
          // Once userConfig is filled, clear the interval and start fetching datasets
          clearInterval(checkUserConfigInterval);
          startFetchingDatasets();
        }
      }, 300);
    } else {
      // If userConfig is already filled, start fetching datasets immediately
      startFetchingDatasets();
    }

    function startFetchingDatasets() {
      fetchDatasetsTask = setInterval(() => {
        dispatch(
          fetchSortedDatasets(isPrivatePath, false, filterData.sortValue) as any
        );
      }, FETCH_ENRICHMENT_PROGRESS_INTERVAL);
      dispatch(
        fetchSortedDatasets(isPrivatePath, true, filterData.sortValue) as any
      );
    }

    return () => clearInterval(fetchDatasetsTask);
    //eslint-disable-next-line
  }, [dispatch, isPrivatePath, userConfig]);

  useEffect(() => {
    if (!enrichingDataset) {
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
      return;
    }

    const { progress, type, name } = enrichingDataset;

    switch (type) {
      case "IN_PROGRESS":
        showToast(
          <VLToastComponent
            title={`Enrichment in progress for ${name}`}
            icon={loadingGif}
            progress={progress}
            status="IN_PROGRESS"
            onClose={handleToastClose}
            description={
              "We have started enriching your dataset. You will be notified once it's ready"
            }
          />
        );
        return;

      case "SUCCESS":
        showToast(
          <VLToastComponent
            status="SUCCESS"
            onClose={() => {
              handleToastClose();
              dispatch(setEnrichingDataset(null));
            }}
            title={`Enrichment is completed for ${name}`}
            description={"Your dataset enrichment completed successfully"}
          />
        );

        return;
    }
    //eslint-disable-next-line
  }, [enrichingDataset]);

  const LoadDataset = (
    datasetId: string,
    datasetName: string,
    datasetSource: string
  ) => {
    amplitudeTrack(AMP_DATASET_OPENED, {
      [AMP_DATASET_NAME]: datasetName,
      [AMP_DATASET_ID]: datasetId,
      [AMP_DATASET_SOURCE]: datasetSource,
    });

    navigate(`/dataset/${datasetId}/data`);
  };

  const createFirstDataset = (isReload: boolean = false) => {
    if (isReload) {
      dispatch(fetchDatasets() as any);
    }
  };

  useEffect(() => {
    if (dropdownBoxDisplay || showDatasetTypeDropdown) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [dropdownBoxDisplay, showDatasetTypeDropdown]);

  return (
    <>
      <Helmet>
        <title>{dynamicTitle}</title>
      </Helmet>
      <Header isPrivatePath={isPrivatePath} />
      <div className={styles.datasetContainer}>
        <div className={styles.datasetCountDark}>{renderInventoryInfo()}</div>
        <div
          ref={cardContainer}
          className={
            isContentLoading || length < 1
              ? styles.cardSectionWithoutScroll
              : styles.CardSection
          }
          data-testid="dataset-inventory-cards-container"
        >
          {isPrivatePath ? (
            <AddDatasetCard isContentLoading={isContentLoading} user={user} />
          ) : null}

          {length < 1 && isAnyFilterSelected && !isContentLoading && (
            <NoMatchFoundCard
              handleClearFilters={handleClearFilters}
              isInventory={true}
            />
          )}
          {filteredDatasets.map((dataset, i) => {
            return (
              <DatasetCard
                key={i}
                {...{
                  dataset,
                  cardElement,
                  LoadDataset,
                  deleteModalId,
                  isPrivatePath,
                  userId,
                  setCardWitdh,
                }}
              />
            );
          })}

          {count > 0 &&
            [...Array(count)].map((_, index) => (
              <div key={index} className={styles.cardPlaceHolder}>
                <div
                  className={styles.cardPlaceHolderData}
                  data-testid={
                    isContentLoading && "dataset_inventory-card-loader"
                  }
                >
                  {isContentLoading && <ContentLoad radius="33px" />}
                </div>
              </div>
            ))}
        </div>

        <CreateDatasetModal createFirstDataset={createFirstDataset} />
        <LimitExceedModal />
        <UpgradePlanModal />
        <ProPlanModal />
        <FreePlanModal />
        <SignupModal />
        <SignupSuccessModal />
        {showRequestDemoModal?.flag && (
          <RequestDemoModal calledFrom={showRequestDemoModal.calledFrom} />
        )}
        <DatasetErrorModal />
        <InternalServerErrorModal />
        <ErrorModal
          hideIcon
          showModal={showDSCreationWarning}
          title="UI Dataset Uploads Not Supported Yet"
          message={
            <div>
              At this time, creating datasets through the UI is not supported in
              On-Prem environments. This functionality will be available soon.
              <br />
              <br />
              <span className={styles.bold}>
                In the meantime, please follow the guide below to create your
                dataset.
              </span>
            </div>
          }
          onClose={() => dispatch(setShowDSCreationWarning(false))}
          actionButton1={
            <div
              className={styles.onPremGuideBtn}
              onClick={handleShowOnPremGuide}
            >
              On-Prem: Create Dataset Guide
            </div>
          }
        />
      </div>
    </>
  );
};

function mapStatesToProps(state: State) {
  return {
    datasets: getDatasets(state),
    userConfig: getUserConfig(state),
    sampleData: getSampleData(state),
    filterData: getFilterData(state),
    deleteModalId: getDeleteModalId(state),
    isContentLoading: getContentLoading(state),
    showRequestDemoModal: getShowRequestDemoModal(state),
    userId: getUserInfo(state).userId || null,
    enrichingDataset: getEnrichingDataset(state),
    showDSCreationWarning: getShowDSCreationWarning(state),
  };
}

export default connect(mapStatesToProps)(DatasetInventory);

type DatasetInventoryTypes = {
  datasets: Dataset[];
  deleteModalId: string;
  isContentLoading: boolean;
  showDSCreationWarning: boolean;
  showRequestDemoModal: { flag: boolean; calledFrom: ContactUsComponentType };
  userConfig: any[];
  userId: string | null;
  filterData: InventoryFilterData;
  enrichingDataset: null | EnrichingDS;
};
